/*
  LinkForm.jsx
*/

// Npm.
import React from 'react';

// Lib.
import cleanWhiteSpace from '../../../utils/cleanWhiteSpace';
import getUniqueLinkId from '../../../utils/getUniqueLinkId';

// Components.
import TextField from '../../TextField';
import TextFieldOnBlur from '../../TextFieldOnBlur';
import Selector from '../../Selector';
import Button from '../../Button';
import CheckboxWithLabel from '../../CheckboxWithLabel';
import PeopleGenerationField from './PeopleGenerationField';
import EditJSONButton from './EditJSONButton';
import PostProcessingForm from './PostProcessingForm';

// Hooks.
import useReadOnly from '../../../hooks/useReadOnly';

// Makes.
import { makeStyles } from '../../../makes/makeStyles';

// Constants.
import { DEFAULT_NODE_COLOR } from '../../../utils/constants';

// Local constants.
const NOOP = () => {};

// Helper to get an option's style for the node selectors.
function getOptionStyle(node) {
  return { color: node._botech?.color || DEFAULT_NODE_COLOR };
}

// Helper to get node properties.
// At this point, the links might be strings and unmutated by force-graph,
// so we check if the node is a string first for some of these.
function getNodeLabel(node) {
  if (typeof node === 'string') {
    return node || 'untitled';
  }
  return node._botech?.label || 'untitled';
}
function getNodeId(node) {
  if (typeof node === 'string') {
    return node;
  }
  return node.id || '';
}

// Helpers for checking optional link properties.
function methodHasThresholdValue(value) {
  return value === 'simple_threshold'
    || value === 'single_threshold'
    || value === 'complex_threshold';
}
function methodHasRunsForNYears(value) {
  return value === 'schedule';
}
function methodHasValueToUse(value) {
  return value === 'inflow';
}

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    '& > .section': {
      marginBottom: theme.space(4),
      '& > .title': {
        color: theme.color_primary_main,
        marginBottom: theme.space(2),
      }
    }
  },
  fieldContainer: {
    marginTop: theme.space(2),
  },
  smallFieldContainer: {
    marginTop: theme.space(1),
  },
  actionsContainer: {
    '& > div': {
      marginTop: theme.space(1),
      '&:first-of-type': {
        marginTop: 0,
      }
    }
  },
}));

/**
 * Link editing form.
 */
export default function LinkForm({
  className,
  disabled,
  link,
  graphData,
  onChangeGraphData,
  onChangeSelectedLink
}) {

  const readOnly = useReadOnly();
  const classes = useStyles();
  const botech = link._botech || {};
  const isSelfLink = link.source === link.target;

  // Handlers.

  const updateLink = readOnly ? NOOP : (fields) => {
    const nextLink = { ...link, ...fields };
    if (onChangeGraphData) {
      onChangeGraphData(data => ({
        ...data,
        links: data.links.map(obj => obj === link ? nextLink : obj),
      }));
    }
    if (onChangeSelectedLink) {
      onChangeSelectedLink(nextLink);
    }
  };

  function handleFocusId(event) {
    event.target.select();
  }

  function handleChangeSource(event) {
    const node = graphData.nodes.find(node => node.id === event.target.value);
    if (node) {
      updateLink({ source: node });
    }
  }

  function handleChangeTarget(event) {
    const node = graphData.nodes.find(node => node.id === event.target.value);
    if (node) {
      updateLink({ target: node });
    }
  }

  function handleChangeBotech(value) {
    updateLink({ _botech: value || {} });
  }

  function handleClickReverse() {
    updateLink({ source: link.target, target: link.source });
  }

  const handleClickDuplicate = readOnly ? NOOP : () => {
    const nextLink = { ...link, id: getUniqueLinkId(graphData) };
    if (onChangeGraphData) {
      onChangeGraphData(data => ({ ...data, links: data.links.concat(nextLink) }));
    }
  };

  const handleClickDelete = readOnly ? NOOP : () => {
    if (onChangeGraphData) {
      onChangeGraphData(data => ({
        ...data,
        links: data.links.filter(obj => obj !== link),
      }));
    }
    if (onChangeSelectedLink) {
      onChangeSelectedLink(null);
    }
  };

  // Editing botech metadata handlers.

  function updateBotech(fields) {
    if (fields) {
      updateLink({ _botech: { ...link._botech, ...fields } });
    }
  }

  function handleChangeLabel(value) {
    updateBotech({ label: cleanWhiteSpace(value) });
  }

  function handleChangeOrder(value) {
    const n = value ? Number(value) : null;
    updateBotech({ order: typeof n === 'number' && !Number.isNaN(n) ? n : null });
  }

  function handleChangeValueToTargetOperator(event) {
    updateBotech({ value_to_target_operator: event.target.value });
  }

  function handleChangeSetBySurrogate(event) {
    updateBotech({ set_by_surrogate: event.target.checked });
  }

  function handleChangeRemoveValueFromSource(event) {
    updateBotech({ remove_value_from_source: event.target.checked });
  }

  function handleChangeRequiresSourceBalance(event) {
    updateBotech({ requires_source_balance: event.target.checked });
  }

  // function handleChangeSupplier(event) {
  //   updateBotech({ supplier: event.target.checked });
  // }

  function handleChangePeopleGeneration(value) {
    updateBotech({ generate_array: value });
  }

  // Trigger values and handlers.

  const trigger = botech.trigger || {};
  const method = trigger.method || '';
  const parameters = trigger.parameters || {};

  function handleChangeMethod(event) {

    const method = event.target.value;
    const nextBotech = { ...link._botech };

    // Check for empty method.
    if (!method) {
      delete nextBotech.trigger;
      updateLink({ _botech: nextBotech });
      return;
    }

    // Delete parameters if they won't be visible in the form anymore.
    const prevMethod = nextBotech.trigger?.method;
    const nextParameters = { ...parameters };
    if (!methodHasThresholdValue(method)) {
      delete nextParameters.threshold_value;
    }
    if (!methodHasRunsForNYears(method)) {
      delete nextParameters.initially_runs_for_n_years;
      delete nextParameters.additional_runs_every_n_years;
    }
    if (!methodHasValueToUse(method)) {
      delete nextParameters.value_to_use;
    } else if (!methodHasValueToUse(prevMethod)) {
      // Requires default.
      // https://github.com/ForecastHealth/botech-online-editor/issues/24
      nextParameters.value_to_use = 'inflow';
    }

    // Update the trigger with new method and parameters.
    // If there are no parameters, delete the parameters object.
    nextBotech.trigger = { ...nextBotech.trigger, method };
    if (Object.keys(nextParameters).length > 0) {
      nextBotech.trigger.parameters = nextParameters;
    } else {
      delete nextBotech.trigger.parameters;
    }
    updateLink({ _botech: nextBotech });

  }

  function updateParameters(fields) {
    const nextBotech = { ...link._botech };
    nextBotech.trigger = { ...nextBotech.trigger };
    nextBotech.trigger.parameters = { ...nextBotech.trigger.parameters, ...fields };
    updateLink({ _botech: nextBotech });
  }

  return (
    <div className={classes.root}>

      <div className="section">
        <p className="title overline">
          Link Properties
        </p>
        <div>
          <TextField
            label="Link ID"
            placeholder="None"
            value={link.id || ''}
            onFocus={handleFocusId}
            readOnly
            fullWidth
          />
        </div>
        <div className={classes.fieldContainer}>
          <TextFieldOnBlur
            label="Label"
            placeholder={`${link.source?._botech?.label || link.source?.id || '_'} → ${link.target?._botech?.label || link.target?.id || '_'}`}
            value={botech.label || ''}
            onChange={handleChangeLabel}
            fullWidth
            maxLength={128}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        <div className={classes.fieldContainer}>
          <Selector
            label="Source"
            value={getNodeId(link.source)}
            onChange={handleChangeSource}
            fullWidth
            disabled={disabled}
            readOnly={readOnly}
          >
            <optgroup label="Source">
              {
                graphData.nodes.filter(node => node !== link.target).map(node => {
                  const id = getNodeId(node);
                  return (
                    <option key={id} value={id} style={getOptionStyle(node)}>
                      { getNodeLabel(node) }
                    </option>
                  );
                })
              }
            </optgroup>
            <optgroup label="Target">
              <option value={getNodeId(link.target)}>{ getNodeLabel(link.target) }</option>
            </optgroup>
          </Selector>
        </div>
        <div className={classes.fieldContainer}>
          <Selector
            label="Target"
            value={getNodeId(link.target)}
            onChange={handleChangeTarget}
            fullWidth
            disabled={disabled}
            readOnly={readOnly}
          >
            <optgroup label="Source">
              <option value={getNodeId(link.source)}>{ getNodeLabel(link.source) }</option>
            </optgroup>
            <optgroup label="Target">
              {
                graphData.nodes.filter(node => node !== link.source).map(node => {
                  const id = getNodeId(node);
                  return (
                    <option key={id} value={id} style={getOptionStyle(node)}>
                      { getNodeLabel(node) }
                    </option>
                  );
                })
              }
            </optgroup>
          </Selector>
        </div>
        <div className={classes.fieldContainer}>
          <TextFieldOnBlur
            label="Order"
            type="number"
            placeholder="None"
            value={typeof botech.order === 'number' ? String(botech.order) : ''}
            onChange={handleChangeOrder}
            fullWidth
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        {/*<div className={classes.supplierContainer}>
          <CheckboxWithLabel
            label="Supplier"
            checked={botech.supplier === true}
            onChange={handleChangeSupplier}
            disabled={disabled}
          />
        </div>*/}
        <div className={classes.fieldContainer}>
          <Selector
            label="Value to target Operator"
            value={botech.value_to_target_operator || 'multiply'}
            onChange={handleChangeValueToTargetOperator}
            fullWidth
            disabled={disabled}
            readOnly={readOnly}
          >
            <option value="add">Add</option>
            <option value="multiply">Multiply</option>
          </Selector>
        </div>
      </div>

      <div className="section">
        <div className={classes.smallFieldContainer}>
          <CheckboxWithLabel
            label="Set by Surrogate"
            fullWidth
            checked={botech.set_by_surrogate === true}
            onChange={handleChangeSetBySurrogate}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        <div className={classes.smallFieldContainer}>
          <CheckboxWithLabel
            label="Remove Value from Source"
            fullWidth
            checked={botech.remove_value_from_source ?? true /* True by default */}
            onChange={handleChangeRemoveValueFromSource}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        <div className={classes.smallFieldContainer}>
          <CheckboxWithLabel
            label="Requires Source Balance"
            fullWidth
            checked={botech.requires_source_balance ?? true /* True by default */}
            onChange={handleChangeRequiresSourceBalance}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
      </div>

      <div className="section">
        <p className="title overline">
          Postprocessing
        </p>
        <PostProcessingForm
          botech={botech}
          onChangeBotech={handleChangeBotech}
          disabled={disabled}
          readOnly={readOnly}
        />
      </div>

      <div className="section">
        <p className="title overline">
          Trigger
        </p>
        <div className={classes.fieldContainer}>
          <Selector
            label="Method"
            value={method}
            onChange={handleChangeMethod}
            fullWidth
            disabled={disabled}
            readOnly={readOnly}
          >
            <option value="">None</option>
            <option value="inflow">Inflow</option>
            <option value="simple_threshold">Simple Threshold</option>
            <option value="schedule">Schedule</option>
            <option value="complex_threshold">Complex Threshold</option>
            <option value="single_threshold">Single Threshold</option>
          </Selector>
        </div>
        {
          methodHasThresholdValue(method) &&
          <div className={classes.fieldContainer}>
            <TextFieldOnBlur
              label="Threshold Value"
              value={typeof parameters.threshold_value === 'number' ? String(parameters.threshold_value) : ''}
              onChange={(value) => updateParameters({ threshold_value: (value && Number(value)) || 0 })}
              fullWidth
              disabled={disabled}
              type="number"
              placeholder="0"
              min="1"
              readOnly={readOnly}
            />
          </div>
        }
        {
          methodHasRunsForNYears(method) &&
          <>
            <div className={classes.fieldContainer}>
              <TextFieldOnBlur
                label="Initially Runs For N Years"
                value={typeof parameters.initially_runs_for_n_years === 'number' ? String(parameters.initially_runs_for_n_years) : ''}
                onChange={(value) => updateParameters({ initially_runs_for_n_years: (value && Number(value)) || 0 })}
                fullWidth
                disabled={disabled}
                type="number"
                placeholder="0"
                min="0"
                readOnly={readOnly}
              />
            </div>
            <div className={classes.fieldContainer}>
              <TextFieldOnBlur
                label="Additional Runs Every N Years"
                value={typeof parameters.additional_runs_every_n_years === 'number' ? String(parameters.additional_runs_every_n_years) : ''}
                onChange={(value) => updateParameters({ additional_runs_every_n_years: (value && Number(value)) || 0 })}
                fullWidth
                disabled={disabled}
                type="number"
                placeholder="0"
                min="0"
                readOnly={readOnly}
              />
            </div>
          </>
        }
        {
          methodHasValueToUse(method) &&
          <div className={classes.fieldContainer}>
            <Selector
              label="Value To Use"
              value={parameters.value_to_use || 'inflow'}
              onChange={(event) => updateParameters({ value_to_use: event.target.value })}
              fullWidth
              disabled={disabled}
              readOnly={readOnly}
            >
              <option value="inflow">Inflow</option>
              <option value="balance">Balance</option>
            </Selector>
          </div>
        }
      </div>

      <div className="section">
        <p className="title overline">
          Generate Array
        </p>
        <PeopleGenerationField
          label="Generate Array"
          hasComplement
          nodes={graphData.nodes}
          value={botech.generate_array}
          onChange={handleChangePeopleGeneration}
          disabled={disabled}
          readOnly={readOnly}
        />
      </div>

      {
        !readOnly &&
        <div className="section">
          <p className="title overline">
            Link Actions
          </p>
          <div className={classes.actionsContainer}>
            <div>
              <EditJSONButton
                value={botech}
                onChange={handleChangeBotech}
                disabled={disabled}
                fullWidth
              >
                Edit Raw Link
              </EditJSONButton>
            </div>
            <div>
              <Button onClick={handleClickDuplicate} disabled={disabled} fullWidth>
                Duplicate Link
              </Button>
            </div>
            <div>
              <Button onClick={handleClickReverse} disabled={disabled || isSelfLink} fullWidth>
                Reverse Link
              </Button>
            </div>
            <div>
              <Button onClick={handleClickDelete} disabled={disabled} fullWidth>
                Delete Link
              </Button>
            </div>
          </div>
        </div>
      }
      
    </div>
  );
}
